import type { UrlMedia } from './types';

export interface MediaVariantPayload {
  base_media_id: string;
  variant_media_id: string;
  target_platforms: string[];
  slot_index: number;
}

const SPEC_KEY_PREFIX_TO_PLATFORM: Array<[string, string]> = [
  ['instagram_', 'instagram'],
  ['facebook_', 'facebook'],
  ['twitter_', 'twitter'],
  ['tiktok_', 'tiktok'],
  ['linkedin_', 'linkedin'],
  ['youtube_', 'youtube'],
  ['pinterest_', 'pinterest'],
  ['threads_', 'threads'],
  ['bluesky_', 'bluesky'],
];

function inferPlatformFromVariantKey(key: string): string | null {
  if (!key) {
    return null;
  }
  const normalized = key.trim().toLowerCase();
  for (const [prefix, platform] of SPEC_KEY_PREFIX_TO_PLATFORM) {
    if (normalized.startsWith(prefix)) {
      return platform;
    }
  }
  const exactMatch = SPEC_KEY_PREFIX_TO_PLATFORM.find(([, platform]) => platform === normalized);
  return exactMatch?.[1] || null;
}

export function extractPlatformVariants(
  platformVersions: Record<string, unknown> | undefined,
  baseMediaId?: string,
): Record<string, string> {
  const variants: Record<string, string> = {};
  if (!platformVersions) {
    return variants;
  }

  Object.entries(platformVersions).forEach(([key, rawValue]) => {
    if (!rawValue || typeof rawValue !== 'object' || Array.isArray(rawValue)) {
      return;
    }
    const entry = rawValue as { media_id?: unknown };
    const mediaId = typeof entry.media_id === 'string' ? entry.media_id : '';
    if (!mediaId || (baseMediaId && mediaId === baseMediaId)) {
      return;
    }
    const platform = inferPlatformFromVariantKey(key);
    if (!platform) {
      return;
    }
    variants[platform] = mediaId;
  });

  return variants;
}

type VariantCarrier = Pick<UrlMedia, 'mediaId' | 'platformVariants'>;

export function resolveEffectiveMediaIdForPlatform(
  item: VariantCarrier,
  platform: string,
): string | undefined {
  return item.platformVariants?.[platform] || item.mediaId || undefined;
}

export function buildMediaVariantsPayload(
  items: VariantCarrier[],
  slotIndexOffset = 0,
): MediaVariantPayload[] {
  const payload: MediaVariantPayload[] = [];

  items.forEach((item, index) => {
    if (!item.mediaId || !item.platformVariants) {
      return;
    }

    const groupedPlatforms = new Map<string, string[]>();
    Object.entries(item.platformVariants).forEach(([platform, variantMediaId]) => {
      if (!platform || !variantMediaId || variantMediaId === item.mediaId) {
        return;
      }
      const platforms = groupedPlatforms.get(variantMediaId) || [];
      if (!platforms.includes(platform)) {
        platforms.push(platform);
      }
      groupedPlatforms.set(variantMediaId, platforms);
    });

    groupedPlatforms.forEach((targetPlatforms, variantMediaId) => {
      payload.push({
        base_media_id: item.mediaId!,
        variant_media_id: variantMediaId,
        target_platforms: targetPlatforms,
        slot_index: slotIndexOffset + index,
      });
    });
  });

  return payload;
}
